/* eslint-disable no-unused-vars */
import React, { useCallback } from 'react';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { plusicon } from '../../components/imagepath';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { matchPermissions } from '../../utils/Permission';
import AdvancedTable from '../../components/AdvancedTable';
import { GetStaffListApi, UpdateStaffProfileApi } from '../../utils/api';
import { notification, Switch } from 'antd';
import { PERMISSIONS } from '../../constants';

const StaffList = () => {
  const { userDetails } = useSelector(state => state?.auth);
  const matchPermissionsForUser = matchPermissions(userDetails);
  const [loadingStates, setLoadingStates] = React.useState({});
  const [refreshKey, setRefreshKey] = React.useState(0);

  // Permissions
  const canView = matchPermissionsForUser(PERMISSIONS.STAFF.VIEW);
  const canCreate = matchPermissionsForUser(PERMISSIONS.STAFF.CREATE);
  const canUpdate = matchPermissionsForUser(PERMISSIONS.STAFF.UPDATE);
  const canExport = matchPermissionsForUser(PERMISSIONS.STAFF.EXPORT);
  const canStatusUpdate = matchPermissionsForUser(PERMISSIONS.STAFF.STATUS_UPDATE);

  // Handle status toggle for individual staff member
  const handleStatusToggle = async (staffId, currentStatus) => {
    if (!canStatusUpdate) {
      notification.error({
        message: 'Permission Denied',
        description: 'You do not have permission to update staff status.',
      });
      return;
    }

    setLoadingStates(prev => ({ ...prev, [staffId]: true }));
    try {
      const response = await UpdateStaffProfileApi({
        Query: staffId,
        obj: { isActive: !currentStatus },
      });

      if (response?.status === 200) {
        notification.success({
          message: 'Status Updated',
          description: `Staff member is now ${!currentStatus ? 'active' : 'inactive'}.`,
        });
        // Trigger table refresh
        setRefreshKey(prev => prev + 1);
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating staff status:', error);
      notification.error({
        message: 'Update Failed',
        description: error?.response?.data?.message || 'Failed to update staff status. Please try again.',
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [staffId]: false }));
    }
  };

  // Define columns for AdvancedTable
  const columns = [
    {
      id: 'name',
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      cell: (row) => (
        <Link to={`/staff-list/staff-profile/${row?.id}`}>{row?.name || '---'}</Link>
      ),
    },
    {
      id: 'type',
      name: 'Department',
      selector: row => row.type,
      sortable: true,
    },
    {
      id: 'phoneNo',
      name: 'Mobile',
      selector: row => row.phoneNo,
      sortable: false,
    },
    {
      id: 'email',
      name: 'Email',
      selector: row => row.email,
      sortable: true,
    },
    {
      id: 'orderIndex',
      name: 'Order Index',
      selector: row => row.orderIndex,
      sortable: true,
    },
    {
      id: 'isActive',
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      cell: (row) => (
        <Switch
          checked={row.isActive !== false}
          onChange={() => handleStatusToggle(row.id, row.isActive !== false)}
          loading={loadingStates[row.id]}
          disabled={!canStatusUpdate}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          size="default"
        />
      ),
    },
    {
      id: 'createdAt',
      name: 'Created At',
      selector: row => row.createdAt,
      sortable: true,
      cell: (row) => (
        <span>{(row.createdAt && moment(row.createdAt).format('DD-MM-YYYY')) || '---'}</span>
      ),
    },
    {
      id: 'actions',
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex align-items-center">
          {canUpdate && (
            <Link
              to={`/staff-list/staff-profile/edit-profile/${row.id}`}
              className="btn btn-sm btn-white text-success me-2"
            >
              <FeatherIcon icon="edit-3" className="feather-edit-3" />
            </Link>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // Define filter configuration
  const filterConfig = [
    {
      id: 'name',
      label: 'Name',
      type: 'text',
      placeholder: 'Search by name...',
    },
    {
      id: 'phoneNo',
      label: 'Mobile',
      type: 'text',
      placeholder: 'Search by mobile number...',
    },
    {
      id: 'email',
      label: 'Email',
      type: 'text',
      placeholder: 'Search by email...',
    },
    {
      id: 'isActive',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
    {
      id: 'type',
      label: 'Department',
      type: 'select',
      options: [
        { value: 'Doctor', label: 'Doctor' },
        { value: 'Therapist', label: 'Therapist' },
        { value: 'Nurse', label: 'Nurse' },
        { value: 'Receptionist', label: 'Receptionist' },
        { value: 'Admin', label: 'Admin' },
        { value: 'Manager', label: 'Manager' },
        { value: 'Accountant', label: 'Accountant' },
        { value: 'Other', label: 'Other' },
      ],
      multiple: true,
      searchable: true,
    },
  ];

  // Fetch data function for AdvancedTable
  const fetchStaffData = useCallback(async ({ page, perPage, sortField, sortOrder, search, ...filters }) => {
    try {
      // Check if any filters are applied (excluding search)
      const hasFilters = Object.values(filters).some(value => {
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
      });

      // Build query parameters
      const params = new URLSearchParams();

      // Only add pagination if no filters are applied
      if (!hasFilters) {
        params.append('page', page);
        params.append('limit', perPage);
      } else {
        // When filters are applied, fetch all results
        params.append('page', 1);
        params.append('limit', 10000); // Large number to get all results
      }

      // Add search if present
      if (search) {
        params.append('search', search);
      }

      // Add sorting if present
      if (sortField) {
        params.append('sortBy', sortField);
        params.append('sortOrder', sortOrder === 'asc' ? 'asc' : 'desc');
      }

      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });

      const queryString = `?${params.toString()}`;

      // Call the API
      const response = await GetStaffListApi({ Query: queryString });

      if (response?.status === 200) {
        const responseData = response?.data?.data?.[0] || {};
        return {
          data: responseData.docs || [],
          total: responseData.totalDocs || 0,
        };
      }

      return { data: [], total: 0 };
    } catch (error) {
      console.error('Error fetching staff data:', error);
      return { data: [], total: 0 };
    }
  }, []);

  return (
    <>
      <Header />
      <Sidebar id="menu-item1" id1="menu-items1" />
      <>
        <div className="page-wrapper">
          <div className="content">
            {/* Page Header */}
            <div className="page-header">
              <div className="row">
                <div className="col-sm-12">
                  <ul className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/staff-list">Staff</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <i className="feather-chevron-right">
                        <FeatherIcon icon="chevron-right" />
                      </i>
                    </li>
                    <li className="breadcrumb-item active">Staff List</li>
                  </ul>
                </div>
              </div>
            </div>
            {/* /Page Header */}
            <div className="row">
              <div className="col-sm-12">
                <div className="card card-table show-entire">
                  <div className="card-body">
                    {/* Table Header */}
                    <div className="page-table-header mb-2">
                      <div className="row align-items-center">
                        <div className="col">
                          <div className="doctor-table-blk">
                            <h3>Staff List</h3>
                            <div className="doctor-search-blk"></div>
                            <div className="add-group">
                              {canCreate && (
                                <Link
                                  to="/staff-list/add-staff"
                                  className="btn btn-primary add-pluss ms-2"
                                >
                                  <img src={plusicon} alt="#" />
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="table-responsive">
                        {canView ? (
                          <AdvancedTable
                            key={refreshKey}
                            resource="admin-user"
                            columns={columns}
                            fetchData={fetchStaffData}
                            enableSearch={false}
                            enableFilters={true}
                            enableRefresh={true}
                            enableExport={canExport}
                            enableColumnManager={true}
                            filterConfig={filterConfig}
                            defaultPageSize={10}
                            exportFileName="staff-list"
                            tableId="staff-list-table"
                            persistState={false}
                            stickyHeader={false}
                            striped={true}
                            highlightOnHover={true}
                          />
                        ) : (
                          <div className="permission-denied-message">
                            You do not have permission to view the staff list.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </>
  );
};

export default StaffList;
